import React, { useEffect, useRef, useState } from "react";

import resourceService from "@plannel/services/data/resource-service";
import { Autocomplete, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, Popover, TextField } from "@mui/material"
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { ArrowDropDown, ArrowDropUp, Clear, DoneAll } from "@mui/icons-material";

const REDUX_RESOURCE_FILTER = "RESOURCE_FILTER";
const ResourceFilter = ({ viewName, resourceValue, onChangeFilter, isMultiSelect, isCustomerResourceFilter, customResourceFilterList, resFilters = [] }) => {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const resourceDataListReduxState = reduxUtil.getViewState(REDUX_RESOURCE_FILTER);

  const [resourceDataList, setResourceDataList] = useState(resourceDataListReduxState?.resource ? resourceDataListReduxState.resource : []);
  const [resourceFilterDataList, setResourceFilterDataList] = useState([]);

  const [selectedResource, setSelectedResource] = useState(resourceValue || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef();

  const handleClick = (event) => {
    inputRef.current.focus()

    setAnchorEl(event.currentTarget);
  };

  const handleMouseEnter = () => {
    setIsFocused(true);
  };

  const handleMouseLeave = () => {
    setIsFocused(false);
  };

  const onChangeHandler = (_, value) => {
    onChangeFilter(value);

    const createResourceReduxState = reduxUtil.createObjectState(viewName, { resource: value });
    reduxDispatch(reduxUtil.updateViewState(createResourceReduxState));
  }

  /* 다중선택도 redux 에 저장해야 탭 복귀 시 선택값이 복원된다. */
  const handleClose = () => {
    onChangeHandler(null, selectedResource);
    setAnchorEl(null);
  };

  const onClearHandler = () => {
    onChangeHandler(null, []);
    setSelectedResource([]);
  }

  const onMultiSelectChangeHandle = (event, resObj) => {
    const selectedResCd = resObj?.resCd; // resource code key
    const selectedResName = event.target.name;
    const selectedResNameString = String(selectedResName); // resource name String
    const checkedValue = event.target.checked; // boolean value

    if (checkedValue) {
      const addAndSortResource = [...selectedResource, { resCd: selectedResCd, name: resObj.name }];
      // sort 알파벳순서
      addAndSortResource.sort((a, b) => a?.name.localeCompare(b?.name));

      setSelectedResource(addAndSortResource);
    } else {
      const resourceFilter = selectedResource.filter(value => value.name !== selectedResNameString);

      setSelectedResource(resourceFilter);
    }
  };

  const isCheckValue = (resObj) => {
    const key = resObj.resCd;

    for (const resource of selectedResource) {
      if (isEqual(resource.resCd, key)) {
        return true;
      }
    }

    return false;
  }

  const mutlselectedTextValue = () => {
    if (isEmpty(selectedResource)) {
      return "";
    }

    const selectedResNames = selectedResource.map(res => res.name);
    return selectedResNames.join(", ");
  }
  
  /* Resource Filter 필요시 구현 */
  const resourceTypeFilter = (results) => {
    let resultsFilter = [];

    if (!isEmpty(resFilters)) {
      for (const result of results) {
        if (resFilters.includes(result.resType)) {
          resultsFilter.push(result);
        }
      }
    } else {
      resultsFilter = results;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    let existSelectedValueInResults = [];
    if (isMultiSelect) {
      // selectedResource 는 { resCd, name } 객체 배열 — resCd 로 비교해 목록에 남아있는 선택만 유지
      existSelectedValueInResults = selectedResource.filter(selected => resultsFilter.some(result => isEqual(result.resCd, selected?.resCd)));
    } else {
      existSelectedValueInResults = resultsFilter.find(result => isEqual(result.id, resourceValue?.id));
    }

    if (isEmpty(existSelectedValueInResults)) {
      onChangeHandler(null, isMultiSelect ? [] : null);
    } else {
      onChangeHandler(null, existSelectedValueInResults);
    }

    return resultsFilter;
  }

  const getResourceData = () => {
    /* 커스텀 필터가 존재하는경우 커스텀 필터데이터를 사용함. */
    if (!isNil(isCustomerResourceFilter) && isCustomerResourceFilter) {
      setResourceFilterDataList(customResourceFilterList);
      setResourceDataList(customResourceFilterList);
      onChangeHandler(null, []);
      return;
    }

    /* 이전에 불러온 Dropdown data list가 존재하는경우 다시 가져오지 않음. */
    if (isEmpty(resourceDataListReduxState?.resource)) {
      let resourceResultData = [];
      
      resourceService.getLookup()
        .then(response => {
          const { results } = response.data;
          if (!isEmpty(results)) {
            resourceResultData = results;
          }

          const filterResourceList = resourceTypeFilter(resourceResultData);
          setResourceFilterDataList(filterResourceList);
          setResourceDataList(resourceResultData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createResourceDataListReduxState = reduxUtil.createObjectState(REDUX_RESOURCE_FILTER, { resource: resourceResultData });
          reduxDispatch(reduxUtil.updateViewState(createResourceDataListReduxState));
        });
    } else {
      setResourceFilterDataList(resourceTypeFilter(resourceDataList));
    }
  }

  useEffect(() => {
    getResourceData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customResourceFilterList])

  useEffect(() => {
    if (isMultiSelect) {
      setSelectedResource(resourceValue || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceValue]);

  if (isMultiSelect) {
    return (
      <>
        <FormControl
          className={"resource-multi-select-filter"}
          size="small"
          sx={{ mr: 1, minWidth: 180, width: 200 }}
        >
          <TextField
            size="small"
            label={t("resource")}
            type="string"
            onClick={(e) => handleClick(e)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            value={mutlselectedTextValue()}
            inputRef={inputRef}
            autoComplete="off"
            InputProps={{
              endAdornment: mutlselectedTextValue()?.length > 0 ? (
                <>
                  {isFocused ? <IconButton size="small" aria-label="clear text" onClick={(e) => {
                    e.stopPropagation(); // 부모 onClick 호출 방지
                    onClearHandler();
                  }}>
                    <Clear sx={{ fontSize: '1.2rem' }} />
                  </IconButton> : null}
                  <InputAdornment position="end">
                    <IconButton aria-label="search" sx={{ padding: 0 }} size="small">{open ? <ArrowDropUp />
                      : <ArrowDropDown />}</IconButton>
                  </InputAdornment>
                </>
              ) : <InputAdornment position="end">
                <IconButton aria-label="search" size="small" >{open ? <ArrowDropUp />
                  : <ArrowDropDown />}</IconButton>
              </InputAdornment>
            }}
          />
        </FormControl>
        <Popover open={Boolean(open)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disableAutoFocus
          disableEnforceFocus
          TransitionProps={{ timeout: 0 }}
        >
          <Box>
            <Box sx={{ minWidth: 180, width: 200, overflowY: "auto", overflowX: "hidden" }}>
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <FormGroup sx={{ ml: 1 }}>
                  {resourceFilterDataList.map((resObj) => (
                    <FormControlLabel
                      key={resObj.resCd}
                      control={
                        <Checkbox checked={isCheckValue(resObj)} onChange={(e) => onMultiSelectChangeHandle(e, resObj)} name={resObj.name} />
                      }
                      label={resObj.name}
                    />))
                  }
                </FormGroup>
              </FormControl>
            </Box>
            <Box>
              <Divider sx={{ backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 0.5, opacity: 0.2 }} />
              <Box
                sx={{
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                <Button
                  variant="contained"
                  startIcon={<DoneAll />}
                  size="small"
                  color="primary"
                  onClick={handleClose} >
                  {t("confirm")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </>
    )
  }

  return (
    <Autocomplete
      key={"resource"}
      name={"resource"}
      options={resourceFilterDataList}
      getOptionLabel={option => option?.name}
      value={resourceValue}
      onChange={onChangeHandler}
      isOptionEqualToValue={(option, value) => option.resCd === value?.resCd}
      size="small"
      sx={{ mr: 1, minWidth: 240 }}
      renderInput={(params) => (
        <TextField {...params} label={t("resource")} />
      )}
    />
  )
}

const reloadResourceFilter = (reduxDispatch) => {
  const resourceReduxFilterState = reduxUtil.getViewState(REDUX_RESOURCE_FILTER);

  reduxDispatch(reduxUtil.removeViewState(resourceReduxFilterState));
}

export default React.memo(ResourceFilter);
export { reloadResourceFilter };